/**
 * Petit chargeur de données asynchrones.
 *
 * Le portail n'embarque pas de bibliothèque de cache réseau : les écrans sont
 * peu nombreux et chacun charge ses propres données. Ce hook couvre le besoin
 * commun — état de chargement, erreur affichable, rechargement manuel — sans
 * ajouter de dépendance.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../api';

/** État d'un chargement. */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  /** Message d'erreur tel que rédigé par l'API, prêt à afficher. */
  error: string | null;
  /** Relance le chargement. */
  reload: () => void;
}

/** Extrait un message affichable d'une erreur inconnue. */
export function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Une erreur inattendue est survenue.';
}

/**
 * Charge une donnée au montage et à chaque changement de `deps`.
 * La réponse d'un chargement obsolète est ignorée.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // La fonction de chargement est souvent recréée à chaque rendu : on la lit
  // dans une référence pour que seules les dépendances déclarées déclenchent
  // un nouvel appel.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loaderRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // Une requête annulée n'est pas un échec à signaler.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(messageOf(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Les dépendances sont fournies par l'appelant ; `nonce` déclenche les
    // rechargements manuels.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, loading, error, reload };
}
